/**
 *  @swagger
 *  definitions:
 *    GroupParams:
 *      type: object
 *      properties:
 *        Name:
 *          description: Name of the Group
 *          type: string
 *        Image:
 *          description: Image of the Group
 *          type: string
 *        Type:
 *          description: Type of the Group
 *          $ref: '#/components/enums/GroupType'
 *        CrewQuantity:
 *          description: Crew Quantity of the Group
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    GroupCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/GroupParams'
 *        - type: object
 *          required:
 *            - Name
 *            - Type
 *
 *  @swagger
 *  definitions:
 *    GroupQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Group
 *              type: string
 *        - $ref: '#/definitions/GroupParams'
 *
 *  @swagger
 *  definitions:
 *    GroupUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/GroupQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Group:
 *      allOf:
 *        - $ref: '#/definitions/GroupCreateParams'
 *        - $ref: '#/definitions/GroupUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Group:
 *        allOf:
 *          - $ref: '#/definitions/Group'
 *          - type: object
 *            properties:
 *              Groups:
 *                description: Regattas of the Group
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Regatta'
 */

// Enums
import {
  GroupType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $Params = {
  CrewQuantity: null | number;
  Image: null | string;
  Name: string;
  Type: GroupType;
};

export type $CreateParams = $Params;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$Params>;

export type $Associations = {
  Regattas?: Array<$Model<'Regatta'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  CrewQuantity: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(GroupType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Group'> => {
  const Group = stack.define(
    'Group',
    fields(Sequelize),
    {
      tableName: 'Groups',
    },
  );

  Group.associate = (models: $ModelClasses) => {
    models.Group.belongsToMany<'Regatta', 'RegattaGroup'>(
      models.Regatta,
      {
        as: 'Regattas',
        foreignKey: 'GroupId',
        otherKey: 'RegattaId',
        through: models.RegattaGroup,
      },
    );
  };

  Group.addScope(
    'defaultScope',
    {
      order: [['Name', 'ASC']],
    },
  );

  return Group;
});
